import type {
  ConnectionDiagnostics,
  ConnectionPhase,
  ConnectionReason,
  ConnectionStateEvent,
} from './connection-state';
import {
  formatWebRtcConnectionMetrics,
  formatWebRtcFps,
  formatWebRtcMilliseconds,
  formatWebRtcRate,
  formatWebRtcResolution,
  type WebRtcConnectionMetrics,
} from './rtc-path';

/**
 * 可由用户主动复制给支持人员的连接摘要。
 *
 * 这里刻意不接受设备编号、地址、令牌或原始错误文本。诊断信息的价值
 * 来自状态和时间关系，而不是把会话的隐私数据带出浏览器。
 */
export interface SessionSupportDiagnosticsInput {
  client?: string | null;
  version?: string | null;
  mode?: 'control' | 'view' | string | null;
  route?: string | null;
  metrics?: WebRtcConnectionMetrics | null;
  connection?: ConnectionDiagnostics | null;
}

const PHASE_LABELS: Record<ConnectionPhase, string> = {
  idle: '未开始',
  connecting: '正在连接',
  'awaiting-approval': '等待对方确认',
  paired: '已配对，准备画面',
  negotiating: '正在准备画面',
  connected: '已连接',
  recovering: '正在恢复连接',
  stalled: '暂时无响应',
  failed: '连接失败',
  closed: '已结束',
};

const REASON_LABELS: Record<ConnectionReason, string> = {
  manual: '手动操作',
  signaling: '建立连接',
  approval: '等待确认',
  pairing: '配对',
  negotiating: '准备画面',
  network: '网络波动',
  timeout: '响应超时',
  permission: '权限未开启',
  busy: '设备忙碌',
  offline: '设备离线',
  authentication: '登录状态',
  'self-device': '当前设备',
  'remote-ended': '对方结束',
  rejected: '对方未允许',
  version: '版本不匹配',
  unknown: '未知原因',
};

const ROUTE_LABELS = new Set([
  '正在连接',
  '正在连接…',
  '正在建立连接',
  '安全连接',
  'P2P 直连',
  'P2P 穿透',
  'TURN 中继',
  '连接已建立',
  '未连接',
]);

function safeLabel(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const text = value.replace(/[\r\n\t]+/g, ' ').replace(/\s{2,}/g, ' ').trim().slice(0, 80);
  return text || fallback;
}

function routeLabel(value: unknown): string {
  const text = safeLabel(value, '未知');
  return ROUTE_LABELS.has(text) ? text : '未知';
}

function modeLabel(value: SessionSupportDiagnosticsInput['mode']): string {
  return value === 'view' ? '仅观看' : '远程控制';
}

function ageLabel(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value < 0) return '暂无';
  if (value < 1000) return '刚刚';
  const seconds = Math.round(value / 1000);
  if (seconds < 60) return `${seconds} 秒前`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} 分钟前`;
}

function countLabel(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value < 0) return '暂无';
  return String(Math.round(value));
}

function readyLabel(value: boolean | null | undefined): string {
  if (value == null) return '未知';
  return value ? '已就绪' : '未就绪';
}

function stateLabel(connection: ConnectionDiagnostics | null | undefined): string {
  return connection ? PHASE_LABELS[connection.phase] : '未建立连接';
}

function reasonLabel(connection: ConnectionDiagnostics | null | undefined): string {
  return connection ? REASON_LABELS[connection.reason] : '暂无';
}

function historyLabel(history: readonly ConnectionStateEvent[] | undefined): string {
  if (!history?.length) return '暂无';
  return history
    .slice(-12)
    .map((event) => `${PHASE_LABELS[event.phase]} · ${REASON_LABELS[event.reason]}`)
    .join(' → ');
}

/** 生成不包含令牌、密码、设备编号、地址和原始协议错误的纯文本摘要。 */
export function formatSessionSupportDiagnostics(input: SessionSupportDiagnosticsInput = {}): string {
  const connection = input.connection;
  const metrics = input.metrics;
  const resolution = metrics
    ? formatWebRtcResolution(metrics.videoWidth, metrics.videoHeight)
    : connection
      ? formatWebRtcResolution(connection.videoWidth, connection.videoHeight)
      : '检测中';
  const lines = [
    'LinkDesk 连接摘要',
    `客户端：${safeLabel(input.client, 'Web')}`,
    `版本：${safeLabel(input.version, '未知')}`,
    `模式：${modeLabel(input.mode)}`,
    `状态：${stateLabel(connection)}`,
    `状态原因：${reasonLabel(connection)}`,
    `连接方式：${routeLabel(input.route)}`,
    `连接质量：${metrics ? formatWebRtcConnectionMetrics(metrics) : '检测中'}`,
    `画面：${readyLabel(connection?.streamReady)}`,
    `控制通道：${readyLabel(connection?.controlReady)}`,
    `远程输入：${readyLabel(connection?.remoteInputReady)}`,
    `响应速度：${metrics ? formatWebRtcMilliseconds(metrics.controlRttMs ?? metrics.rttMs) : '检测中'}`,
    `操作确认：${formatWebRtcMilliseconds(connection?.inputRttMs)}`,
    `画面刷新：${metrics ? formatWebRtcFps(metrics.framesPerSecond) : '检测中'}`,
    `画面尺寸：${resolution}`,
    `发送：${metrics ? formatWebRtcRate(metrics.uploadKbps) : '检测中'}`,
    `接收：${metrics ? formatWebRtcRate(metrics.downloadKbps) : '检测中'}`,
    `自动恢复次数：${countLabel(connection?.reconnectCount)}`,
    `最后一次有效数据：${ageLabel(connection?.lastActivityAgeMs)}`,
    `控制通道最后响应：${ageLabel(connection?.lastControlActivityAgeMs)}`,
    `远端画面最后更新：${ageLabel(connection?.lastVideoActivityAgeMs)}`,
    `状态轨迹：${historyLabel(connection?.stateHistory)}`,
  ];

  // 编解码实现不是普通用户需要看到的内容，但对“画面清晰度异常”排查很有价值。
  // 它们只来自浏览器的统计字段，不包含地址或会话内容。
  if (connection?.videoCodec) lines.push(`画面编码：${safeLabel(connection.videoCodec, '未知')}`);
  if (connection?.videoImplementation) lines.push(`画面实现：${safeLabel(connection.videoImplementation, '未知')}`);
  return lines.join('\n');
}
